<template>
  <tr class="announcement-item">
    <td align="right">
      <v-btn :disabled="!ready && !canWrite" :loading="loading"
       @click="edit" icon small>
        <v-icon>mdi-pencil</v-icon>
      </v-btn>
      <Editor :show="showEditor" @close="showEditor=false"
        @updated="loadStream" :data="data" :srcURL="audioSrc"/>
      <v-dialog v-model="noWriteDlg" max-width="600">
        <v-alert type="warning" class="mb-0" border="left"
          colored-border icon="mdi-alert-circle">
          <v-row>
            <v-col>{{ $t('announce.noWritePerm') }}</v-col>
            <v-col class="shrink">
              <v-btn @click="noWriteDlg=false" color="orange" icon outlined>
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </v-dialog>
      <audio ref="player" :src="audioSrc" preload="metadata" @timeupdate="timeUpdate" />
      <v-btn small icon @click="play" :loading="loading" class="mr-1">
        <v-icon>{{ playIcon }}</v-icon>
      </v-btn>
      <v-btn small icon @click="rewind" :disabled="disableRewind">
        <v-icon>mdi-stop</v-icon>
      </v-btn>
    </td>
    <td width="260" v-if="!$vuetify.breakpoint.mobile">
      <v-slider :disabled="!ready" @change="setTime" always-dirty :value="playProg" class="pt-6 ml-10">
      </v-slider>
    </td>
    <td width="160" class="ml-10" v-if="!$vuetify.breakpoint.mobile">
      <div class="mt-2">

        <av-circle v-show="audioSrc && ready"
          ref="avc"
          :canv-width="60"
          :canv-height="60"
          :line-color="graphColor"
          :progress-color="graphColor"
          :playtime-color="graphColor"
          :outline-color="graphColor"
          :bar-color="graphColor"
          :playtime="true"
          :progress="true"
          :audio-src="audioSrc"
          ref-link="player">
        </av-circle>

        <div v-if="errorLoad" align="center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="orange" v-bind="attrs" v-on="on">
                mdi-alert-circle-outline
              </v-icon>
            </template>
            <span>{{ $t('announce.loadError') }}</span>
          </v-tooltip>
        </div>
      </div>
    </td>

  </tr>
</template>

<script>
import { mapGetters } from 'vuex'
import API from '@/api'
import Editor from './editor'
export default {
  name: 'announcement-item',
  data () {
    return {
      showEditor: false,
      noWriteDlg: false,
      loading: false,
      playing: false,
      audioSrc: null,
      disableRewind: true,
      graphColor: '#2196f3',
      lineWidth: 2,
      curTime: 0,
      durTime: 0,
      playProg: 0,
      ready: true,
      errorLoad: false,
      newSrcURL: null
    }
  },
  components: { Editor },
  props: {
    data: {
      type: Object
    }
  },
  computed: {
    ...mapGetters({
      canWriteAll: 'permissions/announceWrite',
      canWriteFor: 'permissions/announceWriteFor'
    }),
    canWrite: function () {
      // global write permission overrides per item permission
      return this.canWriteAll || this.canWriteFor(this.data?.id)
    },
    name: function () {
      return this.data?.name
    },
    pin: function () {
      return this.data?.pin
    },
    lang: function () {
      return this.data?.lang
    },
    playIcon: function () {
      return this.playing
        ? 'mdi-pause'
        : 'mdi-play'
    }
  },
  methods: {
    loadStream: function () {
      this.loading = true
      API.getAnnouncementStream(this.data?.id).then(({ data, status }) => {
        if (!data || status !== 200) {
          throw new Error(`Failed to get audio for announcement id ${this.data?.id}`)
        }
        this.audioSrc = URL.createObjectURL(new Blob([data], { type: 'audio/ogg' }))
        this.$refs.player.onloadedmetadata = () => { this.initCanvas() }
      }).catch(err => {
        this.dbg(err)
        this.dbg(err?.response)
        this.ready = false
        this.errorLoad = true
      }).finally(() => {
        this.loading = false
      })
    },
    initCanvas: function () {
      const ctx = this.$refs.avc?.ctx
      if (!ctx) return
      const w = ctx.canvas.width
      const h = ctx.canvas.height
      ctx.beginPath()
      ctx.strokeStyle = this.graphColor
      ctx.lineWidth = this.lineWidth
      ctx.moveTo(0, h / 2)
      ctx.lineTo(w, h / 2)

      ctx.arc(w / 2, h / 2, 5, 0, 2 * Math.PI)
      ctx.fillStyle = this.graphColor
      ctx.fill()
      ctx.stroke()
      this.durTime = this.$refs.player.duration
    },
    play: function () {
      if (!this.audioSrc) {
        this.loadStream()
      }
      if (this.playing) {
        this.$refs.player.pause()
      } else {
        this.$refs.player.play()
      }
      this.playing = !this.playing
    },
    rewind: function () {
      this.$refs.player.pause()
      this.playing = false
      this.$refs.player.currentTime = 0
      this.$refs.player.onloadedmetadata = () => { this.initCanvas() }
    },
    timeUpdate: function (val) {
      const cur = val.target.currentTime
      const dur = val.target.duration
      this.playProg = Math.round(cur / dur * 100)
      this.disableRewind = !(cur > 0)
      this.curTime = cur
      if (this.$refs.player.ended) {
        this.playing = false
        this.$refs.player.pause()
        this.playProg = 0
        this.disableRewind = true
      }
    },
    setTime: function (val) {
      const dur = this.$refs.player.duration
      const fac = val / 100
      this.$refs.player.currentTime = Math.round(dur * fac)
    },
    edit: function () {
      if (this.canWrite) {
        this.showEditor = true
      } else {
        this.noWriteDlg = true
      }
    }
  }
}
</script>

<style scoped>
.audio-graph {
  border-bottom:1px solid navy;
}
</style>
